﻿import type { Feature, FeatureCollection, Point } from 'geojson';
import { GeoJSONSource, Map as MaplibreMap } from 'maplibre-gl';
import { RunwayOBB } from './runway-geometry';

const SOURCE_ID = 'fe-runway-labels-source';
const LAYER_ID = 'fe-runway-labels';

const LABELS_MIN_ZOOM = 12;
const LABELS_TEXT_SIZE = 14;
// Text offset in ems: [along-text-direction, perpendicular-to-text].
const LABELS_TEXT_OFFSET: [number, number] = [0, 1];
const LABELS_TEXT_COLOR = '#ffffff';
const LABELS_HALO_COLOR = '#000000';
const LABELS_HALO_WIDTH = 0.5;

export class RunwayLabelsLayer {
    private map: MaplibreMap | null = null;

    onAdd(map: MaplibreMap, beforeLayerId: string): void {
        this.map = map;

        if (!map.getSource(SOURCE_ID)) {
            map.addSource(SOURCE_ID, {
                type: 'geojson',
                data: emptyCollection()
            });
        }

        if (!map.getLayer(LAYER_ID)) {
            map.addLayer(
                {
                    id: LAYER_ID,
                    type: 'symbol',
                    source: SOURCE_ID,
                    minzoom: LABELS_MIN_ZOOM,
                    layout: {
                        'text-field': ['get', 'label'],
                        'text-size': LABELS_TEXT_SIZE,
                        'text-font': ['Metropolis Bold'],
                        'text-rotate': ['get', 'rotation'],
                        'text-rotation-alignment': 'map',
                        'text-allow-overlap': true,
                        'text-ignore-placement': true,
                        'text-anchor': 'center',
                        'text-padding': 0,
                        'text-offset': LABELS_TEXT_OFFSET
                    },
                    paint: {
                        'text-color': LABELS_TEXT_COLOR,
                        'text-halo-color': LABELS_HALO_COLOR,
                        'text-halo-width': LABELS_HALO_WIDTH
                    }
                },
                beforeLayerId
            );
        }
    }

    onRemove(map: MaplibreMap): void {
        if (map.getLayer(LAYER_ID)) {
            map.removeLayer(LAYER_ID);
        }
        if (map.getSource(SOURCE_ID)) {
            map.removeSource(SOURCE_ID);
        }
        this.map = null;
    }

    update(obbs: RunwayOBB[]): void {
        const map = this.map;
        if (!map) {
            return;
        }

        const source = map.getSource(SOURCE_ID) as GeoJSONSource | undefined;
        if (!source) {
            return;
        }

        const features: Feature<Point>[] = [];

        for (const obb of obbs) {
            const baseId = obb.properties.baseid as string | undefined;
            const recipId = obb.properties.recipid as string | undefined;
            if (!baseId && !recipId) {
                continue;
            }

            // Determine which end is "base" and which is "recip"
            const { baseEnd, baseRotation, recipEnd, recipRotation } = resolveEnds(obb);

            if (baseId) {
                features.push(createLabelFeature(baseEnd, baseId, baseRotation));
            }
            if (recipId) {
                features.push(createLabelFeature(recipEnd, recipId, recipRotation));
            }
        }

        source.setData({ type: 'FeatureCollection', features });
    }

    clear(): void {
        const source = this.map?.getSource(SOURCE_ID) as GeoJSONSource | undefined;
        source?.setData(emptyCollection());
    }
}

function resolveEnds(obb: RunwayOBB): {
    baseEnd: [number, number];
    baseRotation: number;
    recipEnd: [number, number];
    recipRotation: number;
} {
    const baseId = obb.properties.baseid as string | undefined;
    const recipId = obb.properties.recipid as string | undefined;
    const baseHeading = baseId ? parseDesignatorHeading(baseId) : null;
    const recipHeading = recipId ? parseDesignatorHeading(recipId) : null;

    let baseIsEndA = true;
    if (baseHeading != null) {
        const diffToBase = angleDiff(obb.bearingDeg, baseHeading);
        const diffToRecip = recipHeading != null ? angleDiff(obb.bearingDeg, recipHeading) : 180;
        baseIsEndA = diffToBase < diffToRecip;
    }

    const endARotation = obb.bearingDeg;
    const endBRotation = (obb.bearingDeg + 180) % 360;

    return {
        baseEnd: baseIsEndA ? obb.endA : obb.endB,
        baseRotation: baseIsEndA ? endARotation : endBRotation,
        recipEnd: baseIsEndA ? obb.endB : obb.endA,
        recipRotation: baseIsEndA ? endBRotation : endARotation
    };
}

function createLabelFeature(position: [number, number], label: string, rotation: number): Feature<Point> {
    return {
        type: 'Feature',
        geometry: { type: 'Point', coordinates: position },
        properties: { label, rotation }
    };
}

function parseDesignatorHeading(designator: string): number | null {
    const match = designator.match(/^(\d{1,2})/);
    if (!match) {
        return null;
    }
    return parseInt(match[1], 10) * 10;
}

function angleDiff(a: number, b: number): number {
    let d = Math.abs(a - b) % 360;
    if (d > 180) {
        d = 360 - d;
    }
    return d;
}

function emptyCollection(): FeatureCollection {
    return { type: 'FeatureCollection', features: [] };
}
